import { showMessageError } from '@/shared/lib';
import { useMergeState } from '@/shared/lib/customHooks';
import { useEffect } from 'react';
import { checkUsingHighSamplingRateDevice } from '../../lib/checkUsingHighSamplingRateDevice';
import { isEndOfLife } from '../../lib/isEndOfLife';
import { formatDataDevice } from '../../lib/deviceInfoDrawer';

import fetchQuickAccessDevice from '../../api/services/fetchQuickAccessDevice';
import type { DeviceInfoDrawerProps } from './deviceInfoDrawer.types';

/**
 * Supplies the quick-access device drawer with its device details, end-of-life and high sampling
 * rate flags, and scroll state. Keeps merged local state, fetches and formats the device each time
 * the drawer becomes visible with a device id, and exposes a scroll handler for the drawer body.
 *
 * @param visible - Whether the drawer is open.
 * @param deviceId - Identifier of the device shown in the drawer.
 * @returns An object carrying the drawer state and the scroll handler.
 */
export const useDeviceInfoDrawer = ({
  visible,
  deviceId,
}: Pick<DeviceInfoDrawerProps, 'visible' | 'deviceId'>) => {
  const [state, setState] = useMergeState({
    data: null as ReturnType<typeof formatDataDevice> | null,
    isEOL: false,
    isScroll: false,
    isLoading: false,
    isHighSamplingRate: false,
  });

  /**
   * Loads the details shown in the quick-access device drawer. Sets the loading flag, fetches the device, formats it for display and derives end-of-life status from the first study date plus high sampling rate support from the last sync schema; failures are logged, surfaced as an error message and leave the drawer without data.
   *
   * @param deviceId - Identifier of the device to load.
   * @returns Resolves after the drawer data or the failure state is stored.
   */
  const handleFetchDataDevice = async (deviceId: any) => {
    try {
      setState({ isLoading: true });
      const device = await fetchQuickAccessDevice(deviceId);
      const data = formatDataDevice(device);
      setState({
        data,
        isEOL: isEndOfLife(device?.firstStudyDate),
        isHighSamplingRate: checkUsingHighSamplingRateDevice(device?.lastSync?.schema),
        isLoading: false,
      });
    } catch (error: any) {
      console.error('Failed to fetch device: ', error);
      showMessageError({ content: error.message });
      setState({
        data: null,
        isLoading: false,
      });
    }
  };

  /**
   * Tracks whether the device info drawer body is scrolled so its header can show a divider. Flips the isScroll flag when the scroll target moves off or back to the top.
   *
   * @param e - Scroll event from the drawer body.
   * @returns Nothing.
   */
  const handleScroll = (e: any) => {
    const { scrollTop } = e.target;
    if (scrollTop === 0 && state.isScroll) {
      setState({ isScroll: false });
    } else if (scrollTop !== 0 && !state.isScroll) {
      setState({ isScroll: true });
    }
  };

  useEffect(() => {
    if (deviceId && visible) {
      handleFetchDataDevice(deviceId);
    }
  }, [visible]);

  return { state, handleScroll };
};
